// src/pages/DeviceTypeManagement/UnassignedDevicesTab.jsx
// Shows devices with unassigned or default (caro_agent) device types
// Allows linking devices to correct device types based on hardware info

import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  Grid
} from '@mui/material';
import {
  Search as SearchIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  Memory as HardwareIcon,
  Router as DeviceIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  FilterList as FilterIcon,
  ContentCopy as ContentCopyIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { devicesApi, deviceTypesApi } from '../../services/apiClient';

const UnassignedDevicesTab = ({ deviceTypes, onDeviceUpdated }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [hardwareFilter, setHardwareFilter] = useState('all');
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [selectedNewType, setSelectedNewType] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [bulkAssignSelections, setBulkAssignSelections] = useState({});

  // Find the caro_agent device type ID
  const caroAgentTypeId = useMemo(() => {
    const caroAgent = deviceTypes.find(dt =>
      dt.friendlyName?.toLowerCase() === 'caro_agent' ||
      dt.FrontEndName?.toLowerCase() === 'caro_agent'
    );
    return caroAgent?.id;
  }, [deviceTypes]);

  // Device type IDs considered as "unassigned" or default
  const unassignedTypeIds = useMemo(() => {
    const ids = [1, 2]; // Include device types 1 and 2
    if (caroAgentTypeId) {
      ids.push(caroAgentTypeId);
    }
    return ids;
  }, [caroAgentTypeId]);

  // Check if a device type ID is in the unassigned list
  const isUnassignedType = (typeId) => {
    if (!typeId) return true; // null/undefined is unassigned
    const numericId = typeof typeId === 'string' ? parseInt(typeId, 10) : typeId;
    return unassignedTypeIds.includes(numericId);
  };

  const loadUnassignedDevices = async () => {
    setLoading(true);
    try {
      // Fetch all devices - we'll filter client-side for flexibility
      const response = await devicesApi.getAll({ limit: 9999 });
      const allDevices = response?.data?.data || response?.data || [];

      console.log('[UnassignedDevices] Total devices fetched:', allDevices.length);
      console.log('[UnassignedDevices] Unassigned type IDs:', unassignedTypeIds);

      // Filter for unassigned devices (null, undefined, caro_agent, or type 1/2)
      const unassigned = allDevices.filter(device => {
        const typeId = device.deviceTypeModel?.id || device.deviceType || device.deviceTypeId;
        const isUnassigned = isUnassignedType(typeId);

        if (isUnassigned) {
          console.log('[UnassignedDevices] Including device:', device.friendlyName || device.id, 'typeId:', typeId);
        }

        return isUnassigned;
      });

      console.log('[UnassignedDevices] Filtered unassigned devices:', unassigned.length);
      setDevices(unassigned);
    } catch (error) {
      console.error('Error loading unassigned devices:', error);
      enqueueSnackbar('Failed to load devices', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUnassignedDevices();
  }, [unassignedTypeIds]);

  // Parse config safely - handles both object and JSON string
  const parseConfig = (device) => {
    let config = device.config || {};
    if (typeof config === 'string') {
      try { config = JSON.parse(config); } catch { config = {}; }
    }
    return config;
  };

  // Extract hardware info from device config
  // Handles both CARO agent devices (config.hardwareInfo) and UniFi devices (config.model / config.unifiInfo)
  const getHardwareInfo = (device) => {
    const config = parseConfig(device);
    const hardwareInfo = config.hardwareInfo || {};
    const unifiInfo = config.unifiInfo || {};
    return {
      model: config.model || unifiInfo.model || unifiInfo.shortModel || hardwareInfo.model || hardwareInfo.piModel || 'Unknown',
      type: config.type || unifiInfo.deviceType || null,
      piModel: hardwareInfo.piModel || 'N/A',
      cpuArch: hardwareInfo.cpuArch || 'N/A',
      memoryMB: hardwareInfo.memoryMB || 'N/A',
      serial: config.identity?.sn || hardwareInfo.serial || 'N/A',
      mac: config.identity?.mac || config.identifier?.macAddress || null,
      ip: config.identity?.lanIP || config.identifier?.lanIP || null,
      deviceType: unifiInfo.deviceType || config.type || null,
      features: unifiInfo.features || [],
      firmware: unifiInfo.firmwareVersion || null,
      isUnifi: !!(config.unifiInfo || config.type?.includes('UniFi'))
    };
  };

  // Get unique hardware models for filtering
  const uniqueHardwareModels = useMemo(() => {
    const models = new Set();
    devices.forEach(device => {
      const hw = getHardwareInfo(device);
      if (hw.model && hw.model !== 'Unknown') {
        models.add(hw.model);
      }
    });
    return Array.from(models).sort();
  }, [devices]);

  // Filter devices
  const filteredDevices = useMemo(() => {
    return devices.filter(device => {
      const hw = getHardwareInfo(device);
      const name = device.friendlyName || device.id?.toString() || '';
      const ip = hw.ip || '';
      const mac = hw.mac || '';

      // Search filter
      const matchesSearch = !searchTerm ||
        name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        ip.includes(searchTerm) ||
        mac.toLowerCase().includes(searchTerm.toLowerCase()) ||
        hw.model.toLowerCase().includes(searchTerm.toLowerCase());

      // Hardware filter
      const matchesHardware = hardwareFilter === 'all' || hw.model === hardwareFilter;

      return matchesSearch && matchesHardware;
    });
  }, [devices, searchTerm, hardwareFilter]);

  // Group devices by hardware model for suggestions
  const devicesByHardware = useMemo(() => {
    const groups = {};
    filteredDevices.forEach(device => {
      const hw = getHardwareInfo(device);
      const model = hw.model || 'Unknown';
      if (!groups[model]) {
        groups[model] = [];
      }
      groups[model].push(device);
    });
    return groups;
  }, [filteredDevices]);

  // Suggest device type based on hardware
  const suggestDeviceType = (hardwareModel) => {
    const modelLower = hardwareModel.toLowerCase();

    // UniFi device type suggestions
    if (modelLower.includes('usw') || modelLower.includes('switch') || modelLower.includes('us-')) {
      return deviceTypes.find(dt => {
        const name = (dt.friendlyName || '').toLowerCase();
        return name.includes('switch') || name.includes('usw');
      });
    }
    if (modelLower.includes('uap') || modelLower.includes('u6') || modelLower.includes('u7') || modelLower.includes('access point') || modelLower.includes('nanohd') || modelLower.includes('flexhd')) {
      return deviceTypes.find(dt => {
        const name = (dt.friendlyName || '').toLowerCase();
        return name.includes('access point') || name.includes('uap') || name.includes('wifi');
      });
    }
    if (modelLower.includes('udm') || modelLower.includes('dream') || modelLower.includes('gateway') || modelLower.includes('usg') || modelLower.includes('uxg')) {
      return deviceTypes.find(dt => {
        const name = (dt.friendlyName || '').toLowerCase();
        return name.includes('gateway') || name.includes('udm') || name.includes('dream');
      });
    }

    // Raspberry Pi suggestions
    if (modelLower.includes('zero')) {
      return deviceTypes.find(dt =>
        dt.friendlyName?.toLowerCase().includes('raspi_zero') ||
        dt.friendlyName?.toLowerCase().includes('_zero')
      );
    }
    if (modelLower.includes('raspberry pi 3') || modelLower.includes('pi 3')) {
      return deviceTypes.find(dt =>
        dt.friendlyName?.toLowerCase().includes('raspi_b') ||
        dt.friendlyName?.toLowerCase().includes('_raspi_b')
      );
    }
    if (modelLower.includes('duckbill')) {
      return deviceTypes.find(dt =>
        dt.friendlyName?.toLowerCase().includes('duckbill')
      );
    }
    return null;
  };

  const handleOpenAssignDialog = (device) => {
    setSelectedDevice(device);
    const hw = getHardwareInfo(device);
    const suggested = suggestDeviceType(hw.model);
    setSelectedNewType(suggested?.id || '');
    setAssignDialogOpen(true);
  };

  const handleAssignDeviceType = async () => {
    if (!selectedDevice || !selectedNewType) return;

    setAssigning(true);
    try {
      const deviceId = selectedDevice.id || selectedDevice._id;
      await devicesApi.update(deviceId, { deviceType: selectedNewType });

      enqueueSnackbar('Device type updated successfully', { variant: 'success' });
      setAssignDialogOpen(false);
      setSelectedDevice(null);
      setSelectedNewType('');
      loadUnassignedDevices();
      onDeviceUpdated?.();
    } catch (error) {
      console.error('Error assigning device type:', error);
      enqueueSnackbar('Failed to update device type', { variant: 'error' });
    } finally {
      setAssigning(false);
    }
  };

  const handleBulkAssign = async (hardwareModel, deviceTypeId) => {
    const devicesToUpdate = devicesByHardware[hardwareModel] || [];
    if (!devicesToUpdate.length || !deviceTypeId) return;

    const confirmMessage = `Assign ${devicesToUpdate.length} device(s) with hardware "${hardwareModel}" to the selected device type?`;
    if (!window.confirm(confirmMessage)) return;

    setAssigning(true);
    try {
      let successCount = 0;
      for (const device of devicesToUpdate) {
        try {
          const deviceId = device.id || device._id;
          await devicesApi.update(deviceId, { deviceType: deviceTypeId });
          successCount++;
        } catch (err) {
          console.error(`Failed to update device ${device.id}:`, err);
        }
      }

      enqueueSnackbar(`Updated ${successCount}/${devicesToUpdate.length} devices`, {
        variant: successCount === devicesToUpdate.length ? 'success' : 'warning'
      });
      loadUnassignedDevices();
      onDeviceUpdated?.();
    } catch (error) {
      console.error('Error in bulk assign:', error);
      enqueueSnackbar('Bulk assign failed', { variant: 'error' });
    } finally {
      setAssigning(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" gutterBottom>
            Unassigned Devices
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Devices with no device type or default "caro_agent" type. Link them to the correct hardware type.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={loadUnassignedDevices}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by name, IP, MAC, or hardware model..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Hardware Model</InputLabel>
              <Select
                value={hardwareFilter}
                onChange={(e) => setHardwareFilter(e.target.value)}
                label="Hardware Model"
              >
                <MenuItem value="all">All Hardware Models</MenuItem>
                {uniqueHardwareModels.map(model => (
                  <MenuItem key={model} value={model}>
                    {model} ({devicesByHardware[model]?.length || 0})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Chip
              label={`${filteredDevices.length} devices`}
              color="primary"
              variant="outlined"
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Hardware Groups with Suggestions */}
      {Object.keys(devicesByHardware).length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <HardwareIcon />
            Hardware Groups & Suggestions
          </Typography>
          <Grid container spacing={2}>
            {Object.entries(devicesByHardware).map(([model, devicesInGroup]) => {
              const suggested = suggestDeviceType(model);
              return (
                <Grid item xs={12} md={6} lg={4} key={model}>
                  <Card variant="outlined">
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Box>
                          <Typography variant="subtitle1" fontWeight={600}>
                            {model}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {devicesInGroup.length} device(s)
                          </Typography>
                        </Box>
                        {suggested ? (
                          <Chip
                            size="small"
                            icon={<CheckIcon />}
                            label="Suggestion available"
                            color="success"
                            variant="outlined"
                          />
                        ) : (
                          <Chip
                            size="small"
                            icon={<WarningIcon />}
                            label="No suggestion"
                            color="warning"
                            variant="outlined"
                          />
                        )}
                      </Box>

                      {suggested && (
                        <Alert severity="info" sx={{ mb: 2, py: 0 }}>
                          Suggested type: <strong>{suggested.FrontEndName || suggested.friendlyName}</strong>
                        </Alert>
                      )}

                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Assign to Device Type</InputLabel>
                          <Select
                            label="Assign to Device Type"
                            value={bulkAssignSelections[model] ?? (suggested?.id || '')}
                            onChange={(e) => {
                              setBulkAssignSelections(prev => ({
                                ...prev,
                                [model]: e.target.value
                              }));
                            }}
                          >
                            <MenuItem value="">
                              <em>Select device type...</em>
                            </MenuItem>
                            {deviceTypes.map(dt => (
                              <MenuItem key={dt.id} value={dt.id}>
                                {dt.FrontEndName || dt.friendlyName}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <Button
                          variant="contained"
                          size="small"
                          disabled={!bulkAssignSelections[model] && !suggested?.id || assigning}
                          onClick={() => {
                            const typeId = bulkAssignSelections[model] ?? suggested?.id;
                            if (typeId) {
                              handleBulkAssign(model, typeId);
                            }
                          }}
                          sx={{ minWidth: 80, height: 40 }}
                        >
                          {assigning ? <CircularProgress size={20} /> : 'Apply'}
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      )}

      {/* Devices Table */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Device</TableCell>
              <TableCell>Hardware Model</TableCell>
              <TableCell>Pi Model</TableCell>
              <TableCell>CPU Arch</TableCell>
              <TableCell>Memory</TableCell>
              <TableCell>IP Address</TableCell>
              <TableCell>Current Type</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredDevices.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    {searchTerm || hardwareFilter !== 'all'
                      ? 'No devices match the current filters'
                      : 'No unassigned devices found'
                    }
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredDevices.map(device => {
                const hw = getHardwareInfo(device);
                const currentType = device.deviceTypeModel?.FrontEndName ||
                                   device.deviceTypeModel?.friendlyName ||
                                   'None';

                return (
                  <TableRow key={device.id} hover>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight={500}>
                          {device.friendlyName || `Device ${hw.mac || 'N/A'}`}
                        </Typography>
                        <span>
                          <Typography variant="caption" color="text.secondary">
                            MAC: {hw.mac || 'N/A'}
                          </Typography>
                          {hw.mac && (
                            <IconButton
                              size="small"
                              color="inherit"
                              onClick={() => navigator.clipboard.writeText(hw.mac)}
                              aria-label="Copy MAC address"
                            >
                              <ContentCopyIcon fontSize="small" />
                            </IconButton>
                          )}
                        </span>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={hw.model}
                        variant="outlined"
                        color={hw.isUnifi ? 'info' : 'default'}
                        icon={<HardwareIcon />}
                      />
                      {hw.type && hw.model === 'Unknown' && (
                        <Typography variant="caption" display="block" color="text.secondary">
                          {hw.type}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{hw.piModel}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{hw.cpuArch}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {hw.memoryMB !== 'N/A' ? `${hw.memoryMB} MB` : 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <Typography variant="body2" fontFamily="monospace">
                        {hw.ip || 'N/A'}
                      </Typography>
                      {hw.ip && (
                        <IconButton
                          size="small"
                          color="inherit"
                          onClick={() => navigator.clipboard.writeText(hw.ip)}
                          aria-label="Copy IP address"
                        >
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      )}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={currentType}
                        color={currentType === 'None' ? 'error' : 'warning'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Assign Device Type">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenAssignDialog(device)}
                          color="primary"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Assign Device Type Dialog */}
      <Dialog open={assignDialogOpen} onClose={() => setAssignDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Assign Device Type</DialogTitle>
        <DialogContent>
          {selectedDevice && (
            <Box sx={{ pt: 1 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Device:</strong> {selectedDevice.friendlyName || `Device ${selectedDevice.id}`}
                </Typography>
                <Typography variant="body2">
                  <strong>Hardware:</strong> {getHardwareInfo(selectedDevice).model}
                </Typography>
                <Typography variant="body2">
                  <strong>IP:</strong> {getHardwareInfo(selectedDevice).ip || 'N/A'}
                </Typography>
              </Alert>

              <FormControl fullWidth>
                <InputLabel>Select Device Type</InputLabel>
                <Select
                  value={selectedNewType}
                  onChange={(e) => setSelectedNewType(e.target.value)}
                  label="Select Device Type"
                >
                  {deviceTypes.map(dt => (
                    <MenuItem key={dt.id} value={dt.id}>
                      {dt.FrontEndName || dt.friendlyName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAssignDeviceType}
            disabled={!selectedNewType || assigning}
            startIcon={assigning ? <CircularProgress size={20} /> : null}
          >
            Assign
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UnassignedDevicesTab;
